import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Maximize2 } from 'lucide-react';
import { galleryPhotos, galleryUi } from '../data/galleryData';
import { useLanguage } from '../context/LanguageContext';
import GalleryLightbox from './GalleryLightbox';

const SLIDE_MS = 5500;

const GallerySection = () => {
  const { language } = useLanguage();
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [hovered, setHovered] = useState(false);
  const [inView, setInView] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const sectionRef = useRef(null);
  const touchX = useRef(null);
  const n = galleryPhotos.length;

  const go = useCallback((d) => setActive((i) => (i + d + n) % n), [n]);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setInView(e.isIntersecting), { threshold: 0.25 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  const running = playing && inView && !hovered && lightbox === null;

  useEffect(() => {
    if (!running) return undefined;
    const id = setTimeout(() => go(1), SLIDE_MS);
    return () => clearTimeout(id);
  }, [running, active, go]);

  const loop = [...galleryPhotos, ...galleryPhotos];

  return (
    <section id="galleria" ref={sectionRef} className="relative py-24 bg-[#0e0e0e] overflow-hidden" data-testid="gallery-section">
      <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#c9a227]/10 blur-[120px] pointer-events-none"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-12">
          <span className="inline-block text-[#c9a227] font-medium tracking-[0.2em] uppercase text-sm mb-4">{galleryUi.subtitle[language]}</span>
          <h2 className="font-serif text-4xl md:text-5xl font-bold text-white mb-4">{galleryUi.title[language]}</h2>
          <div className="w-24 h-0.5 bg-[#c9a227] mx-auto mb-6"></div>
          <p className="text-white/60 max-w-xl mx-auto">{galleryUi.description[language]}</p>
        </div>

        <div
          className="relative max-w-5xl mx-auto h-[68vh] md:h-[74vh] min-h-[420px] rounded-2xl overflow-hidden border border-[#c9a227]/25 shadow-2xl bg-black"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
          onTouchEnd={(e) => {
            if (touchX.current === null) return;
            const dx = e.changedTouches[0].clientX - touchX.current;
            if (Math.abs(dx) > 50) go(dx < 0 ? 1 : -1);
            touchX.current = null;
          }}
          data-testid="gallery-stage"
        >
          {galleryPhotos.map((p, i) => {
            const on = i === active;
            return (
              <div key={p.src} className={`absolute inset-0 transition-opacity duration-[1200ms] ease-in-out ${on ? 'opacity-100 z-10' : 'opacity-0 z-0'}`} aria-hidden={!on}>
                <img src={p.src} alt="" className="absolute inset-0 w-full h-full object-cover blur-2xl scale-125 opacity-50" />
                <div className="absolute inset-0 bg-black/30"></div>
                <button
                  onClick={() => setLightbox(i)}
                  aria-label={galleryUi.open[language]}
                  className="absolute inset-0 flex items-center justify-center p-4 md:p-8 cursor-zoom-in"
                  data-testid={`gallery-slide-${i}`}
                >
                  <img
                    src={p.src}
                    alt={p.caption[language]}
                    className={`max-h-full max-w-full object-contain rounded-lg shadow-[0_20px_60px_rgba(0,0,0,0.6)] ${on ? (i % 2 ? 'kenburns-b' : 'kenburns-a') : ''}`}
                  />
                </button>
              </div>
            );
          })}

          <div className="absolute inset-x-0 bottom-0 z-20 bg-gradient-to-t from-black/90 via-black/50 to-transparent px-5 md:px-10 pt-24 pb-6 pointer-events-none">
            <div key={`${active}-${language}`} className="animate-fadeInUp">
              <div className="w-10 h-0.5 bg-[#c9a227] mb-3"></div>
              <h3 className="font-serif text-2xl md:text-4xl font-bold text-white" data-testid="gallery-caption">{galleryPhotos[active].caption[language]}</h3>
              <p className="text-white/70 text-sm md:text-base mt-1">{galleryPhotos[active].sub[language]}</p>
            </div>
          </div>

          <div className="absolute top-4 left-4 z-20 text-white/80 text-sm font-serif tracking-widest bg-black/40 backdrop-blur px-3 py-1 rounded-full" data-testid="gallery-counter">
            {String(active + 1).padStart(2, '0')} / {String(n).padStart(2, '0')}
          </div>
          <div className="absolute top-4 right-4 z-20 flex gap-2">
            <button onClick={() => setPlaying((p) => !p)} aria-label={playing ? galleryUi.pause[language] : galleryUi.play[language]} data-testid="gallery-toggle-play"
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white hover:bg-[#c9a227] hover:text-[#121212] flex items-center justify-center transition-colors">
              {playing ? <Pause size={16} /> : <Play size={16} />}
            </button>
            <button onClick={() => setLightbox(active)} aria-label={galleryUi.open[language]} data-testid="gallery-open-lightbox"
              className="w-10 h-10 rounded-full bg-black/40 backdrop-blur text-white hover:bg-[#c9a227] hover:text-[#121212] flex items-center justify-center transition-colors">
              <Maximize2 size={16} />
            </button>
          </div>

          <button onClick={() => go(-1)} aria-label={galleryUi.prev[language]} data-testid="gallery-prev"
            className="absolute left-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 backdrop-blur text-white hover:bg-[#c9a227] hover:text-[#121212] flex items-center justify-center transition-colors">
            <ChevronLeft size={24} />
          </button>
          <button onClick={() => go(1)} aria-label={galleryUi.next[language]} data-testid="gallery-next"
            className="absolute right-3 top-1/2 -translate-y-1/2 z-20 w-11 h-11 rounded-full bg-black/40 backdrop-blur text-white hover:bg-[#c9a227] hover:text-[#121212] flex items-center justify-center transition-colors">
            <ChevronRight size={24} />
          </button>

          <div className="absolute bottom-0 inset-x-0 z-30 h-1 bg-white/10">
            {running && <div key={active} className="h-full bg-[#c9a227] origin-left gallery-progress" style={{ animationDuration: `${SLIDE_MS}ms` }}></div>}
          </div>
        </div>

        <div className="flex flex-wrap justify-center gap-2 mt-5" data-testid="gallery-dots">
          {galleryPhotos.map((_, i) => (
            <button key={i} onClick={() => setActive(i)} aria-label={`${i + 1}`} data-testid={`gallery-dot-${i}`}
              className={`h-2 rounded-full transition-all duration-500 ${i === active ? 'w-8 bg-[#c9a227]' : 'w-2 bg-white/25 hover:bg-white/50'}`} />
          ))}
        </div>
      </div>

      <div className="relative mt-14 overflow-hidden marquee-mask" data-testid="gallery-marquee">
        <div className="flex gap-5 w-max gallery-marquee py-6" style={{ animationDuration: `${n * 7}s` }}>
          {loop.map((p, i) => (
            <button
              key={i}
              onClick={() => setLightbox(i % n)}
              aria-label={galleryUi.open[language]}
              className={`group relative flex-shrink-0 w-40 md:w-56 aspect-[3/4] rounded-xl overflow-hidden border border-white/10 hover:border-[#c9a227] transition-all duration-500 hover:-translate-y-2 hover:rotate-1 ${i % 2 ? 'mt-8' : ''}`}
              data-testid={`gallery-thumb-${i}`}
            >
              <img src={p.src} alt={p.caption[language]} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <span className="absolute bottom-3 left-3 right-3 text-left font-serif text-white text-sm md:text-base opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                {p.caption[language]}
              </span>
            </button>
          ))}
        </div>
      </div>

      {lightbox !== null && (
        <GalleryLightbox photos={galleryPhotos} index={lightbox} language={language} onClose={() => setLightbox(null)} onChange={setLightbox} />
      )}
    </section>
  );
};

export default GallerySection;
